import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import GenericNav from '../../../components/genericcomponents/navbars/GenericNav'
import { downloadReport } from '../../../api/outboundorders/download-report'
import {
  Box,
  Backdrop,
  Container,
  CircularProgress,
  Grid,
  Typography,
} from '../../../components/mui/Core/index'
import '../outboundorders.css'
import BillingDetailsCard from '../components/BillingDetailsCard/billing-details-card'
import ShippingDetailsCard from '../components/ShippingDetailsCard/shipping-details-card'
import ShippingDocumentsCard from '../components/ShippingDocumentsCard/shipping-documents-card'
import StatusChip from '../../../components/genericcomponents/StatusChip/status-chip'
import { fetchShipmentDetails } from './order-details-slice'
import DmsOrderDetailsTable from './components/DmsOrderDetailsTable/dms-order-details-table'
import WmsOrderDetailsTable from './components/WmsOrderDetailsTable/wms-order-details-table'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import TableSearch from '../../../components/TableSearch/table-search'
import useBrowserTabTitles from '../../../hooks/browserTabTitles/browser-tab-titles'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import PageTitle from '../../../components/PageTitle/page-title'
import Constants from '../../../constants/en.json'
import { hasUserPermission } from '../../../utils/user-utils'
import { userPermissionConstants } from '../../../constants/user-permission-constants'
import { sessionStorageKeyConstants } from '../../../constants/session-storage-key-constants'
import { useTheme } from '../../../components/mui/Styles/index'
import UploadDocumentModal from '../components/UploadDocumentModal/UploadDocumentModal'
import { fetchOrderDocumentsByOrderId, fetchOrderDocumentTypes } from './order-document-slice'

function OrderDetails() {
  const theme = useTheme()
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const selectedClient = JSON.parse(
    sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT),
  )
  const hasSwitchClientPermission = hasUserPermission({
    permissionCode: userPermissionConstants.SWITCH_CLIENT,
    permissions: authenticatedUser?.permissions,
  })
  const dmsUser = hasSwitchClientPermission ? selectedClient?.dmsFlag : authenticatedUser?.dms
  const dispatch = useDispatch()
  const isFetchingShipmentDetails = useSelector(
    (state) => state.orderDetails.isFetchingShipmentDetails,
  )
  const selectedOrder = useSelector((state) => state.order.selectedOrder)
  const selectedShipment = useSelector((state) => state.orderList.selectedShipment)
  const orderDetails = useSelector((state) => state.orderDetails.orderDetails) || {}
  const orderStatus = selectedShipment?.orderStatus || selectedOrder?.orderStatus
  const orderStatusNotification = orderDetails?.orderStatusNotification
  const fetchShipmentDetailsError = useSelector(
    (state) => state.orderDetails.fetchShipmentDetailsError,
  )
  const isFetchingOrderDocumentTypes = useSelector(
    (state) => state.orderDocument.isFetchingOrderDocumentTypes,
  )
  const isUploadingOrderDocument = useSelector(
    (state) => state.orderDocument.isUploadingOrderDocument,
  )
  const isFetchingOrderDocuments = useSelector(
    (state) => state.orderDocument.isFetchingOrderDocuments,
  )
  const orderDocuments = useSelector((state) => state.orderDocument.orderDocuments)

  const [downloadStatus, setDownloadStatus] = useState('')
  const searchByOptions = [
    { id: 'itemNumber', text: 'NDC/Item #', type: 'string' },
    { id: 'itemDescription', text: 'Item Description', type: 'string' },
    { id: 'lotNumber', text: 'Lot #', type: 'string' },
    { id: 'serialNumbers', text: 'Serial #', type: 'string' },
    { id: 'expirationDate', text: 'Expiration Date', type: 'date' },
    { id: 'trackingNumber', text: 'Tracking #', type: 'string' },
    { id: 'itemHoldCode', text: 'Item Code', type: 'string' },
  ]
  const [tableFilterValue, setTableFilterValue] = useState('')
  const [tableFilterBy, setTableFilterBy] = useState(searchByOptions[0].id)
  const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] = useState(false)

  const isLoading =
    isFetchingShipmentDetails ||
    isFetchingOrderDocumentTypes ||
    isUploadingOrderDocument ||
    isFetchingOrderDocuments

  let pageTitle
  if (dmsUser === 'Y') {
    pageTitle = selectedClient?.clientShortName
      ? `${selectedClient?.clientShortName} - ${Constants.orders.ORDER} ${selectedOrder?.dms_order || selectedShipment?.dms_order} - ${Constants.orders.SHIPMENT} ${selectedShipment?.wmsOrderNum}`
      : `${Constants.orders.ORDER} ${selectedOrder?.dms_order || selectedShipment?.dms_order} - ${Constants.orders.SHIPMENT} ${selectedShipment?.wmsOrderNum}`
  } else {
    pageTitle = selectedClient?.clientShortName
      ? `${selectedClient?.clientShortName} - ${Constants.orders.ORDER} ${selectedOrder?.wms_order || selectedOrder?.wmsOrderNum}`
      : `${Constants.orders.ORDER} ${selectedOrder?.wms_order || selectedOrder?.wmsOrderNum}`
  }

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    setTableFilterValue(inputValue)
    setTableFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }
  const selectedWarehouseBrowserTitle =
    dmsUser === 'Y'
      ? `${Constants.orders.ORDER} ${selectedOrder?.dms_order || selectedShipment?.dms_order} - ${Constants.orders.SHIPMENT} ${selectedShipment?.wmsOrderNum}`
      : `${Constants.orders.ORDER} ${selectedOrder?.wms_order || selectedOrder?.wmsOrderNum}`
  const browserTabTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${selectedWarehouseBrowserTitle}`
    : selectedWarehouseBrowserTitle
  useBrowserTabTitles(browserTabTitle)

  useEffect(() => {
    if (selectedOrder || selectedShipment) {
      dispatch(fetchOrderDocumentTypes())

      const orderId = selectedShipment?.wmsOrderNum || selectedOrder?.wms_order
      if (orderId) {
        dispatch(fetchOrderDocumentsByOrderId(orderId))
      }
    }
  }, [dispatch, selectedOrder, selectedShipment])

  useEffect(() => {
    if (selectedOrder || selectedShipment) {
      const requestArgs = {
        dmsUser,
      }
      dispatch(fetchShipmentDetails(requestArgs))
    }
  }, [dispatch, dmsUser, selectedOrder, selectedShipment])

  useEffect(() => {
    if (fetchShipmentDetailsError) {
      dispatch(
        setSnackbar({
          message: JSON.stringify(fetchShipmentDetailsError.message),
          severity: 'error',
          action: true,
        }),
      )
    }
  }, [fetchShipmentDetailsError, dispatch])

  const handleGetReport = (report, fileType) => {
    /* TODO: Decouple Service and UI layer */
    downloadReport(report, setDownloadStatus, fileType)
  }
  const handleCloseUploadDocumentModal = () => {
    setIsUploadDocumentModalOpen(false)
  }
  const handleOpenUploadDocumentModal = () => {
    setIsUploadDocumentModalOpen(true)
  }

  const titleWrapperStyle = {
    display: 'flex',
  }

  const statusMessageStyle = {
    pb: '10px',
  }

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <GenericNav selected='Orders' user='authenticated' />
        <Backdrop open={isLoading}>
          <CircularProgress color='inherit' />
        </Backdrop>
        <Container maxWidth='xl'>
          <Grid container spacing={3} rowSpacing={0.5}>
            <Grid size={{ xs: 12 }} sx={titleWrapperStyle}>
              <PageTitle title={pageTitle} />
              <Box sx={{ ml: 3, alignSelf: 'center' }}>
                <StatusChip status={orderStatus} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }} sx={statusMessageStyle}>
              <Typography sx={theme.message}>{orderStatusNotification}</Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <BillingDetailsCard orderDetails={orderDetails} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <ShippingDetailsCard orderDetails={orderDetails} />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <ShippingDocumentsCard
                orderDetails={orderDetails}
                handleGetReport={handleGetReport}
                reportDownloadStatus={downloadStatus}
                handleOpenUploadDocumentModal={handleOpenUploadDocumentModal}
                orderDocuments={orderDocuments}
              />
            </Grid>
          </Grid>
          <Grid container maxWidth='xl'>
            <Grid container sx={{ mt: '12px' }}>
              <Grid size={{ xs: 12 }} display='inline'>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <TableSearch returnSearch={returnSearch} searchByOptions={searchByOptions} />
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                {dmsUser === 'Y' ? (
                  <DmsOrderDetailsTable
                    filterBy={tableFilterBy}
                    filterValue={tableFilterValue}
                    searchByOptions={searchByOptions}
                  />
                ) : (
                  <WmsOrderDetailsTable
                    filterBy={tableFilterBy}
                    filterValue={tableFilterValue}
                    searchByOptions={searchByOptions}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Container>
        <GenericSnackbar />
      </Box>
      <UploadDocumentModal
        orderDetails={orderDetails}
        isOpen={isUploadDocumentModalOpen}
        handleClose={handleCloseUploadDocumentModal}
      />
    </>
  )
}

export default OrderDetails
